import { motion } from "framer-motion";

const loadingSignals = [
  { label: "Store ingest", value: "Live" },
  { label: "Signal depth", value: "Founder-grade" },
  { label: "Benchmark set", value: "Top 5" },
];

export const AsoLoadingExperience = ({ progress, activeStatus }) => {
  const steps = [
    "Fetching app metadata",
    "Analyzing ratings and reviews",
    "Extracting ASO keywords",
    "Benchmarking competitors",
    "Evaluating screenshots and creatives",
    "Measuring sentiment trends",
    "Calculating conversion potential",
    "Running Equinox AI Engine",
    "Generating personalized recommendations",
    "Preparing founder video briefing",
  ];

  const circumference = 2 * Math.PI * 90;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const activeStepIndex = Math.max(steps.findIndex((step) => step === activeStatus), 0);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#030408]/94 px-5 py-8 backdrop-blur-2xl" data-testid="aso-loading-overlay">
      <div className="absolute inset-0 overflow-hidden">
        <div className="loader-grid absolute inset-0 opacity-60" />
        <div className="absolute left-[8%] top-[10%] h-40 w-40 rounded-full bg-blue-500/12 blur-3xl" />
        <div className="absolute bottom-[8%] right-[12%] h-52 w-52 rounded-full bg-white/10 blur-3xl" />
        {Array.from({ length: 18 }).map((_, index) => (
          <motion.span
            key={index}
            className="absolute h-2 w-2 rounded-full bg-white/50"
            initial={{ opacity: 0.18, x: `${(index % 6) * 18}vw`, y: `${(index % 5) * 18}vh` }}
            animate={{ opacity: [0.18, 0.8, 0.18], y: [`${(index % 5) * 18}vh`, `${(index % 5) * 18 + 10}vh`] }}
            transition={{ duration: 3 + index * 0.08, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="loader-shell premium-border relative z-10 mx-auto w-full max-w-6xl overflow-hidden rounded-[2.5rem] border border-white/[0.08]"
      >
        <div className="grid gap-0 lg:grid-cols-[0.92fr_1.08fr]">
          <div className="relative overflow-hidden border-b border-white/[0.08] px-6 py-8 sm:px-8 lg:border-b-0 lg:border-r lg:px-10 lg:py-10">
            <div className="loader-copy-wrap absolute inset-0" />
            <div className="ambient-sweep opacity-40" />
            <div className="relative z-10">
              <p className="section-kicker">Equinox ASO AI Engine</p>
              <h2 className="loader-title mt-5 max-w-[10ch] font-display text-4xl tracking-[-0.06em] text-white sm:text-5xl lg:text-6xl">
                Building your founder briefing.
              </h2>
              <p className="mt-6 max-w-xl text-sm leading-8 text-white/[0.64]">
                We&apos;re combining live store metadata, sentiment signals, and benchmark intelligence into a premium growth control room.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="aso-loading-signal-grid">
                {loadingSignals.map((item) => (
                  <div key={item.label} className="rounded-[1.3rem] border border-white/[0.08] bg-white/[0.04] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">{item.label}</p>
                    <p className="mt-2 text-sm font-medium text-white/90">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <div className="flex items-center justify-between gap-4 text-[11px] uppercase tracking-[0.22em] text-white/[0.46]">
                  <span>Analysis progress</span>
                  <span data-testid="aso-loading-status-label">{activeStatus}</span>
                </div>
                <div className="loader-track mt-4 h-2 overflow-hidden rounded-full">
                  <motion.div
                    className="loader-bar h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                  />
                </div>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {steps.map((step, index) => {
                  const isActive = step === activeStatus;
                  const isComplete = index < activeStepIndex;

                  return (
                    <motion.div
                      key={step}
                      initial={{ opacity: 0.35, y: 8 }}
                      animate={{
                        opacity: isActive || isComplete ? 1 : 0.38,
                        y: 0,
                        borderColor: isActive ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
                      }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className={`rounded-[1.2rem] border px-4 py-3 ${isActive ? "bg-white/[0.08]" : "bg-black/20"}`}
                      data-testid={`aso-loading-stage-${index + 1}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`h-2.5 w-2.5 rounded-full ${isActive ? "status-pulse bg-white" : isComplete ? "bg-blue-300" : "bg-white/20"}`} />
                        <span className="text-sm text-white/78">{step}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="relative flex items-center justify-center px-6 py-8 sm:px-8 lg:px-10 lg:py-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.16),transparent_42%)] opacity-90" />
            <div className="float-orb absolute h-72 w-72 rounded-full border border-white/[0.06] bg-white/[0.03] blur-2xl" />
            <div className="relative flex h-[19rem] w-[19rem] items-center justify-center sm:h-[22rem] sm:w-[22rem]">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 24, ease: "linear", repeat: Infinity }}
                className="absolute inset-5 rounded-full border border-dashed border-white/[0.12]"
              />
              <div className="absolute inset-[15%] rounded-full border border-white/[0.06] bg-[radial-gradient(circle,rgba(255,255,255,0.08),transparent_65%)] blur-sm" />
              <svg viewBox="0 0 220 220" className="absolute inset-0 h-full w-full -rotate-90">
                <circle cx="110" cy="110" r="90" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
                <circle
                  cx="110"
                  cy="110"
                  r="90"
                  stroke="url(#aso-gradient-ring)"
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                />
                <defs>
                  <linearGradient id="aso-gradient-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#F8FAFC" />
                    <stop offset="52%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#E7C873" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="relative flex h-40 w-40 items-center justify-center rounded-full border border-white/[0.1] bg-black/35 backdrop-blur-2xl sm:h-44 sm:w-44">
                <div className="text-center">
                  <p className="font-display text-5xl text-white sm:text-6xl" data-testid="aso-loading-progress-value">
                    {Math.round(progress)}%
                  </p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.24em] text-white/[0.42]">Engine synced</p>
                </div>
              </div>

              <div className="absolute left-0 top-10 rounded-full border border-white/[0.08] bg-black/40 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.62]">
                Review signals
              </div>
              <div className="absolute bottom-10 right-0 rounded-full border border-white/[0.08] bg-black/40 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.62]">
                Competitor moat
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};